import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Network, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCIAL_KEYS = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'tiktok'];

export default function AdminImprint() {
  const { authFetch } = useAdmin();
  const [cfg, setCfg] = useState({ enabled: false, headline: 'Naša redakcija', intro: '' });
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/imprint`);
      if (r.ok) {
        const d = await r.json();
        setCfg({ enabled: !!d.enabled, headline: d.headline || 'Naša redakcija', intro: d.intro || '' });
        setAuthors(d.authors || []);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function saveCfg() {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/imprint`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cfg),
      });
      if (r.ok) toast.success('Postavke spremljene');
    } finally { setSaving(false); }
  }

  async function saveAuthor(a) {
    const upd = { ...a };
    const r = await authFetch(`${API}/admin/authors/${a.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(upd),
    });
    if (r.ok) { toast.success(`${a.name} ažuriran`); load(); }
  }

  function updateAuthor(idx, patch) {
    setAuthors(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function setSocial(idx, key, val) {
    setAuthors(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], social_links: { ...(next[idx].social_links || {}), [key]: val } };
      return next;
    });
  }

  if (loading) return <Loader2 className="m-10 animate-spin text-zinc-500" />;

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="admin-imprint">
      <div className="flex items-center gap-3 mb-5">
        <Network className="text-cyan-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold">Impressum (hijerarhija redakcije)</h1>
          <p className="text-sm text-zinc-400">Sinkronizirano sa /admin/authors. Postavi pozicije i hijerarhiju za javnu Impressum stranicu.</p>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 space-y-3 mb-5">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!cfg.enabled}
                 onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
                 data-testid="imprint-enabled-toggle" className="w-4 h-4 accent-cyan-500" />
          <span className="font-bold">Aktiviraj widget na javnoj Impressum stranici</span>
        </label>
        <input value={cfg.headline} onChange={e => setCfg({ ...cfg, headline: e.target.value })}
               placeholder="Naslov bloka (npr. Naša redakcija)" data-testid="imprint-headline-input"
               className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
        <textarea value={cfg.intro} onChange={e => setCfg({ ...cfg, intro: e.target.value })}
                  placeholder="Uvodni tekst" rows={2} data-testid="imprint-intro-input"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
        <button onClick={saveCfg} disabled={saving} data-testid="imprint-save-cfg-btn"
                className="inline-flex items-center gap-1 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 rounded text-sm font-bold disabled:opacity-50">
          {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Spremi postavke
        </button>
      </div>

      <h2 className="text-lg font-bold mb-3">Autori i hijerarhija ({authors.length})</h2>
      <div className="space-y-3">
        {authors.map((a, idx) => (
          <div key={a.id || idx} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`imprint-author-${a.id}`}>
            <div className="flex items-start gap-4">
              {a.picture ? (
                <img src={a.picture} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />
              ) : <div className="w-14 h-14 rounded-full bg-zinc-800 shrink-0" />}
              <div className="flex-1 min-w-0 grid sm:grid-cols-2 gap-3">
                <input value={a.name || ''} disabled
                       className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-bold" />
                <input value={a.position || a.role || ''} placeholder="Pozicija (npr. Glavna urednica)"
                       onChange={e => updateAuthor(idx, { position: e.target.value })}
                       data-testid={`imprint-position-${a.id}`}
                       className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
                <select value={a.parent_id || ''}
                        onChange={e => updateAuthor(idx, { parent_id: e.target.value || null })}
                        data-testid={`imprint-parent-${a.id}`}
                        className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm">
                  <option value="">— Korijen (bez nadređenog) —</option>
                  {authors.filter(x => x.id && x.id !== a.id).map(x => (
                    <option key={x.id} value={x.id}>{x.name} {x.position ? `(${x.position})` : ''}</option>
                  ))}
                </select>
                <input type="number" value={a.hierarchy_order || 0}
                       onChange={e => updateAuthor(idx, { hierarchy_order: +e.target.value })}
                       placeholder="Redoslijed prikaza"
                       data-testid={`imprint-order-${a.id}`}
                       className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 mt-3">
              {SOCIAL_KEYS.map(key => (
                <input key={key} value={(a.social_links || {})[key] || ''}
                       onChange={e => setSocial(idx, key, e.target.value)}
                       placeholder={`${key} URL`}
                       data-testid={`imprint-social-${key}-${a.id}`}
                       className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-xs" />
              ))}
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={() => saveAuthor(a)} data-testid={`imprint-save-${a.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-cyan-700 hover:bg-cyan-600 rounded text-xs font-bold">
                <Save size={12}/> Spremi autora
              </button>
            </div>
          </div>
        ))}
        {authors.length === 0 && (
          <p className="text-center py-10 text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
            Nema autora. Dodajte ih u <a href="/admin/authors" className="underline text-cyan-400">/admin/authors</a>.
          </p>
        )}
      </div>
    </div>
  );
}
